import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { FormControl } from '@mui/material'
import Select, { MultiValue, SingleValue } from 'react-select'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '../../redux/store'
import {
  fetchSecurityCheckFamilyByCategory,
  getSecurityISO,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { useTranslation } from 'react-i18next'
import '../../i18n'

import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import i18n from '../../i18n'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'

interface SelectSecurityProps {
  value: string | string[]
  onChange: (event: SelectChangeEvent<string | string[]>) => void
  multiple?: boolean
}

const SELECT_PAGE_SIZE = 500

const SelectSecurity: React.FC<SelectSecurityProps> = ({ value, onChange, multiple = true }) => {
  const menu = useExclusiveMenu()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const specsLoading = useSelector(selectSpecificationLoading)
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''

  const securityList = useSelector(
    (state: RootState) => state.securitycheckFamily.SecurityCheckFamilyResponse
  )

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [familiesLoading, setFamiliesLoading] = useState(false)


  useEffect(() => {
    // Wait for org NormBase — fetching ISO first races NIS2/NIST and wins on refresh.
    if (specsLoading) return

    let active = true
    setFamiliesLoading(true)
    const language = i18n.language.toLocaleUpperCase()
    const pending = !selectedNorm
      ? dispatch(
          getSecurityISO({
            page: 1,
            pageSize: SELECT_PAGE_SIZE,
            language,
          })
        )
      : (() => {
          const action = fetchSecurityCheckFamilyByCategory({
            category: selectedNorm,
            page: 1,
            pageSize: SELECT_PAGE_SIZE,
            language,
          })
          return action ? dispatch(action) : Promise.resolve()
        })()
    Promise.resolve(pending).finally(() => {
      if (active) setFamiliesLoading(false)
    })
    return () => {
      active = false
    }
  }, [dispatch, selectedNorm, specsLoading, i18n.language])

  const securityCheckFamilies = securityList?.securityCheckFamilies || []

  const options = securityCheckFamilies.map((security) => ({
    value: security._id || '',
    label: security.LabelSCF || '',
  }))

  const selectedOptions = multiple
    ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
    : options.find((opt) => opt.value === value) || null

  const handleSelectChange = (
    selected: MultiValue<{ value: string; label: string }> | SingleValue<{ value: string; label: string }>
  ) => {
    let newValue: string | string[] = ''
    if (multiple) {
      if (Array.isArray(selected)) {
        newValue = selected.map((s) => s.value)
      } else {
        newValue = []
      }
    } else {
      if (selected && !Array.isArray(selected)) {
        newValue = (selected as { value: string; label: string }).value
      } else {
        newValue = ''
      }
    }

    onChange({
      target: { name: 'SecurityCheckFamilyId', value: newValue },
    } as SelectChangeEvent<string | string[]>)
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <FormControl sx={{ width: '100%' }}>
        <Select
          isMulti={multiple}
          value={selectedOptions}
          onChange={handleSelectChange}
          options={options}
          isLoading={specsLoading || familiesLoading}
          placeholder={
            specsLoading || familiesLoading
              ? t('selectOptions.loading')
              : t('Security Check Family')
          }
          noOptionsMessage={() => t('No security check families available')}
          {...menu}
          styles={incyiaSelectStyles}
        />
      </FormControl>
    </div>
  )
}

export default SelectSecurity
